import { HttpClient, HttpContext } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';

import {
  DeployChannelType,
  DeployMessageSendsResponse,
  DeployPlanConfigDetail,
  DeployPlanConfigListResponse,
  DeployPlanMessagesResponse,
  DeployPlanSummaryResponse
} from '@gfe/core/models/deploy-plan-config.model';
import { GBE_BASE_URL } from '@gfe/core/tokens/gbe-url.token';

import { SKIP_GBE_ERROR_NOTIFICATION } from '@core/interceptors/gbe-error.interceptor';
import { GbeEndPointV4 } from '@core/models/gbe/gbe-endpoint.enum';

@Injectable({ providedIn: 'root' })
export class DeployPlanConfigService {
  private readonly http = inject(HttpClient);
  private readonly gbeUrl = inject(GBE_BASE_URL);

  private buildUrl(path: string): string {
    return `${this.gbeUrl}/${path}`;
  }

  /**
   * `skipErrorNotification` is for a background read whose caller copes without the list.
   */
  list$(
    dataspaceId: number,
    options?: { skipErrorNotification?: boolean }
  ): Observable<DeployPlanConfigListResponse> {
    const url = this.buildUrl(GbeEndPointV4.DEPLOY_PLAN_CONFIG);
    return this.http.get<DeployPlanConfigListResponse>(url, {
      params: { data_space_id: dataspaceId.toString() },
      context: options?.skipErrorNotification
        ? new HttpContext().set(SKIP_GBE_ERROR_NOTIFICATION, true)
        : undefined
    });
  }

  getDetail$(id: string): Observable<DeployPlanConfigDetail> {
    const url = this.buildUrl(`${GbeEndPointV4.DEPLOY_PLAN_CONFIG}/${id}`);
    return this.http.get<DeployPlanConfigDetail>(url);
  }

  /**
   * `channelConfig` is shallow-merged onto the stored configuration, so an omitted key is
   * left alone while an explicit value replaces it. Every member is optional because a body
   * carrying one field is valid — a rename sends only `name`.
   *
   * `channel` is always sent: the backend derives which validation class `channel_config` is
   * checked against from it, and without it the whole object is stripped as unrecognised.
   */
  patch$(
    id: string,
    channel: DeployChannelType,
    body: {
      channelConfig?: Record<string, unknown>;
      allowRedeploy?: boolean;
      name?: string;
    }
  ): Observable<DeployPlanConfigDetail> {
    const url = this.buildUrl(`${GbeEndPointV4.DEPLOY_PLAN_CONFIG}/${id}`);
    return this.http.patch<DeployPlanConfigDetail>(url, {
      channel,
      ...(body.channelConfig === undefined ? {} : { channel_config: body.channelConfig }),
      ...(body.allowRedeploy === undefined ? {} : { allow_redeploy: body.allowRedeploy }),
      ...(body.name === undefined ? {} : { name: body.name })
    });
  }

  /**
   * The on/off switch, deliberately apart from `patch$`: it has its own route, demands ADMIN
   * where the general PATCH beside it accepts USER, sends no `channel`, and its one field is
   * required where every member of `patch$`'s body is optional.
   *
   * Answers with the whole configuration, so callers merge the response instead of re-fetching.
   */
  setEnabled$(id: string, isEnabled: boolean): Observable<DeployPlanConfigDetail> {
    const url = this.buildUrl(`${GbeEndPointV4.DEPLOY_PLAN_CONFIG}/${id}/enabled`);
    return this.http.patch<DeployPlanConfigDetail>(url, { is_enabled: isEnabled });
  }

  /**
   * Builds the configuration against real plan data without dispatching anything. Costs a
   * full plan fetch on the backend, so callers cache the result per configuration rather
   * than firing one call per channel on page load.
   */
  messages$(id: string, planId: string): Observable<DeployPlanMessagesResponse> {
    const url = this.buildUrl(`${GbeEndPointV4.DEPLOY_PLAN_CONFIG}/${id}/messages`);
    return this.http.get<DeployPlanMessagesResponse>(url, {
      params: { plan_id: planId }
    });
  }

  /**
   * Every configuration's deploy outcome for a plan, in one cheap read — no pipeline, no plan
   * fetch — which is what lets the deploy page call it on load and after each deploy where
   * `messages$` would be far too expensive to.
   *
   * Plan-rooted rather than configuration-rooted because it answers for all of them at once.
   */
  deploySummary$(planId: string): Observable<DeployPlanSummaryResponse> {
    const url = this.buildUrl(`${GbeEndPointV4.PLANS}/${planId}/deploy-summary`);
    return this.http.get<DeployPlanSummaryResponse>(url);
  }

  /**
   * Send history alone, for settling a table after a deploy: the attempts are all that
   * changed, and `messages$` would re-render every message to return them.
   *
   * `planId` is required — history is per (plan, configuration), so a configuration deployed
   * for two plans has two.
   */
  sends$(id: string, planId: string): Observable<DeployMessageSendsResponse> {
    const url = this.buildUrl(`${GbeEndPointV4.DEPLOY_PLAN_CONFIG}/${id}/sends`);
    return this.http.get<DeployMessageSendsResponse>(url, {
      params: { plan_id: planId }
    });
  }
}
